import { Injectable } from '@angular/core';
import { UserService } from './user.service';
import { LogService } from '../../common/services/log/log.service';
import { AuthConfig, JwksValidationHandler, OAuthService } from 'angular-oauth2-oidc';
import { Claims } from '../../common/types/claims';
import { environment } from '../../../../environments/environment';
import { filter, map } from 'rxjs/operators';
import { Observable } from 'rxjs';
import { IAccessTokenSource } from '@caslink/common-client/ng-app/authentication/AccessTokenSource';

@Injectable({
    providedIn: 'root'
})
export class AuthenticationService implements IAccessTokenSource {

    private _isAuthenticated = false;
    public accessTokenUpdated: Observable<string>;

    constructor(
        private oauthService: OAuthService,
        private userService: UserService,
        private logger: LogService
    ) {

        const authConfig: AuthConfig = {
            issuer: environment.identityServer,
            redirectUri: window.location.origin + '/',
            clientId: environment.clientId,
            scope: environment.oauthScope,
            responseType: 'code'
        };

        oauthService.configure(authConfig);
        this.accessTokenUpdated = oauthService.events
            .pipe(
                filter(x => x.type === 'token_received'),
                map(this.getAccessToken.bind(this))
            );
        oauthService.tokenValidationHandler = new JwksValidationHandler();

        oauthService.showDebugInformation = true;
    }

    public getAccessToken(): string {
        return this.oauthService.getAccessToken();
    }

    public async authenticate(): Promise<boolean> {
        try {
            await this.oauthService.loadDiscoveryDocument();
        } catch (e) {
            this.logger.log(e);
            this._isAuthenticated = false;
            return false;
        }
        await this.oauthService.tryLogin();

        if (!this.oauthService.getAccessToken()) {
            return false;
        }

        try {
            await this.oauthService.loadUserProfile();
        } catch (e) {
            // if user profile cannot be parsed tokens are probably invalid
            return false;
        }
        const claims = this.oauthService.getIdentityClaims() as Claims;

        // deliberately not awaiting the following
        this.userService.initUserInfo(claims);

        return true;
    }

    public async logout() {
        this.userService.claims = undefined;
        this.oauthService.logOut();
    }

    public get isAuthenticated(): boolean {
        return this.oauthService.hasValidAccessToken();
    }
}
